import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "./Resources.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { apiFetch, resolveTenant, cachedTenant } from "../../Helper/Api";
import { brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";

// Properties
const properties = require("../../properties.json");

// Fixed sections, matching ResourceCategory on the backend -- order here is
// display order. `tone` is the dashboard's tile tone (.ui-tile in its
// _ui.scss), so each section reads as its own kind of thing at a glance.
// Labels are i18n keys, read when the page renders (the theme sets the language before that).
const CATEGORIES = [
  { key: "DOWNLOAD", title: "downloads", icon: "download", tone: "is-teal", linkLabel: "download", blurb: "downloadsBlurb" },
  { key: "TUTORIAL", title: "tutorials", icon: "play", tone: "is-gold", linkLabel: "watch", blurb: "tutorialsBlurb" },
  { key: "ARTICLE", title: "articles", icon: "file", tone: "is-green", linkLabel: "read", blurb: "articlesBlurb" },
];

class Resources extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
    items: [],
    loading: true,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${t("resources")} | ${tenant.name}` : properties.site_name;
        this.setState({ tenant });
        return apiFetch("/resources", { query: { branchId: tenant.branchId } });
      })
      .then((items) => this.setState({ items: items || [], loading: false }))
      .catch(() => this.setState({ notFound: true, loading: false }));
  }

  /* The cards arrive after this page has already mounted (the resources are a
     second request), so observing once from the ref would catch nothing: the
     reveal root is on screen while the body is still loading. Everything added
     later is picked up here. */
  componentDidUpdate() {
    this.observeNew();
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
  }

  /* Reveal as each block is reached. Nothing hides until this has run, so a
     page whose script never arrives still reads in full. */
  watchReveal = (root) => {
    if (!root) return;
    this.revealRoot = root;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    if (!this.io) {
      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add("is-in");
            this.io.unobserve(entry.target);
          });
        },
        { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
      );
    }
    this.observeNew();
  };

  // observing an element twice is a no-op, so this can run on every update
  observeNew = () => {
    if (!this.io || !this.revealRoot) return;
    this.revealRoot.querySelectorAll("[data-reveal]:not(.is-in)").forEach((el) => this.io.observe(el));
  };

  render() {
    const { tenant, notFound, items, loading } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const b = brandingOf(tenant);

    // only the sections that actually have something in them
    const sections = CATEGORIES.map((cat) => ({
      ...cat,
      items: items.filter((it) => it.category === cat.key),
    })).filter((cat) => cat.items.length > 0);

    const hasAny = sections.length > 0;

    return (
      <>
        <div className="rs-stage mu mu-ground">
          <div className="rs-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="rs-head">
            <span className="mu-chip"><Icon name="download" className="chipi" /> {t("resources")}</span>
            <h1>{t("resourcesTitle")}</h1>
            <p>{b.tagline ? b.tagline : t("resourcesLead")}</p>

            {!loading && hasAny && (
              <div className="rs-jump">
                {sections.map((cat) => (
                  <a key={cat.key} href={`#${cat.key.toLowerCase()}`} className={`rs-jumpitem ${cat.tone}`}>
                    <Icon name={cat.icon} />
                    {t(cat.title)}
                    <b>{cat.items.length}</b>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rs-body mu" ref={this.watchReveal}>
          {/* the shape of the page before the answer arrives, so it does not
              flash an empty state on every visit */}
          {loading && (
            <div className="rs-cat">
              <div className="rs-grid">
                {[0, 1, 2, 3].map((i) => (
                  <span className="rs-skel" key={i} style={{ "--i": i }}></span>
                ))}
              </div>
            </div>
          )}

          {!loading &&
            hasAny &&
            sections.map((cat) => (
              <section className="rs-cat" key={cat.key} id={cat.key.toLowerCase()} data-reveal>
                <header className={`rs-cathead ${cat.tone}`}>
                  <span className="ic" aria-hidden="true"><Icon name={cat.icon} /></span>
                  <span className="tx">
                    <h2>{t(cat.title)}</h2>
                    <span className="sub">{t(cat.blurb)}</span>
                  </span>
                  <span className="count">{cat.items.length}</span>
                </header>

                <div className="rs-grid">
                  {cat.items.map((item, i) => (
                    <a
                      className={`rs-card ${cat.tone}`}
                      key={item.id}
                      href={item.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-reveal
                      style={{ "--i": i % 4 }}
                    >
                      <span className="ic" aria-hidden="true"><Icon name={cat.icon} /></span>

                      <span className="tx">
                        <h3>{item.title}</h3>
                        {item.description && <p>{item.description}</p>}
                      </span>

                      <span className="go">
                        {t(cat.linkLabel)}
                        <Icon name="arrow" className="arw" />
                      </span>
                    </a>
                  ))}
                </div>
              </section>
            ))}

          {!loading && !hasAny && (
            <div className="rs-empty" data-reveal>
              <span className="ic" aria-hidden="true"><Icon name="download" /></span>
              <b>{t("noResourcesYet")}</b>
              <p>{t("noResourcesText")}</p>
              <Link to="/courses" className="mu-btn is-gold has-arw">
                {t("seeCourses")} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Resources;
